import { Bell01, BellOff01, ChevronDown, Lightning01 } from '@untitled-ui/icons-react/build/cjs';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getDeliveryDefaults, IDeliveryPayload } from 'src/app/Dashboard/notificationDelivery';
import LightingIcon from 'src/assets/icons/Lighting.svg';
import WalLogoIcon from 'src/assets/img/logo/logo-icon.svg';
import BadgeCmp from 'src/components/BadgeCmp';
import StatusDotProfileCmp from 'src/components/StatusDotProfileCmp';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import ActivityDropdownList, { IActivityDisplayItem } from 'src/components/V3DropdownCmp/ActivityDropdownList';
import { PATH } from 'src/constants/path';
import useActivityList from 'src/hooks/useActivityList';
import useOrgBranding from 'src/hooks/useOrgBranding';
import { currentUser as currentUserSelector } from 'src/redux/auth.slice';
import { appearance as appearanceSelector } from 'src/redux/basic.slice';
import { useAppSelector } from 'src/redux/hooks';
import { AppDispatch, RootState } from 'src/redux/store';
import { INotificationTypes } from 'src/services/CustomerService';
import { logError } from 'src/services/LoggingService';
import { saveOrderNotificationSettings, updateOrderPriority } from 'src/services/OrderService';
import { mapActivitiesToDisplayItems } from 'src/utils/activityFeed.helpers';
import WalToast from 'src/utils/WalToast';

import NotificationSettingsModal from '../Dashboard/NotificationSettingsModal';

import ShipperConsigneeBox from './ShipperConsigneeBox';
import { ITrackOrder, updateTrackOrder } from './track.slice';
import { getTrackStatusStrip } from './track.status';
import { getBadgeType, getOrderTypeBadge } from './track.utils';

interface IProps {
    order: ITrackOrder;
    index: number;
    onHover?: () => void;
    onHoverEnd?: () => void;
}

const getShortName = (name: string) =>
    name
        .split(' ')
        .slice(0, 2)
        .map((n) => n[0]?.toUpperCase() ?? '')
        .join('');

const TrackOrderCard = ({ order, index, onHover, onHoverEnd }: IProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const appearance = useAppSelector(appearanceSelector);
    const carrierNameRef = useRef<HTMLParagraphElement>(null);
    const trackingLinkRef = useRef<HTMLAnchorElement>(null);
    const orderIdRef = useRef<HTMLParagraphElement>(null);
    const statusLabelRef = useRef<HTMLParagraphElement>(null);

    // Feature gate: resolved 3-tier visibility comes from the listRadarOrders API response → track.portalVisibility
    const isTextNotificationFeatureVisible = useSelector((state: RootState) => state.track.portalVisibility?.textNotification !== false);
    const currentUser = useAppSelector(currentUserSelector);

    const [isHovered, setIsHovered] = useState(false);
    const [isPriorityLoading, setIsPriorityLoading] = useState(false);
    const [textNotificationData, setTextNotificationData] = useState<any>(order?.textNotification ?? null);
    const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
    const [isLatestActivityTooltipOpen, setIsLatestActivityTooltipOpen] = useState(false);

    // "Latest Activity" popover — lazy-fetched when the dropdown opens and cursor-paginated, so
    // older activity loads as you scroll inside it (same as wal-frontend-v2's radar map sidebar).
    //
    // entityId MUST be masterOrderAutoIncrementId (the master order's id) — a radar card represents
    // a LEG, and the leg id has no activity feed of its own.
    const {
        activities: orderActivities,
        isLoading: isActivityLoading,
        isLoadingMore: isActivityLoadingMore,
        hasMore: hasMoreActivity,
        loadMore: loadMoreActivity,
    } = useActivityList({ entityType: 'order', entityId: order.masterOrderAutoIncrementId, enabled: isLatestActivityTooltipOpen });

    const activityDisplayItems = useMemo<IActivityDisplayItem[]>(() => mapActivitiesToDisplayItems(orderActivities), [orderActivities]);
    const carrierName = order.carrier?.name ?? '';
    const orgBranding = useOrgBranding();
    // Org presented as the carrier (masked or unassigned) — show the WAL icon, never initials
    const isOrgCarrier = !carrierName || carrierName === orgBranding.name || carrierName === 'Western Alliance';

    // Pre-fill: order-wise settings first; fall back to user's global notification settings.
    // Mirrors the exact pattern in OrderView/index.tsx.
    const notificationExistingData = useMemo(() => {
        const orderWise = (textNotificationData as any)?.notificationTypes?.notificationTypes;
        if (orderWise) return orderWise;

        return currentUser?.notificationSettings?.notificationTypes ?? null;
    }, [textNotificationData, currentUser?.notificationSettings]);

    const isNotificationEnabled = useMemo(() => {
        const notifData = textNotificationData as any;

        if (notifData) {
            // Order-level notificationTypes JSONB
            if (notifData.notificationTypes?.notificationTypes) {
                return Object.values(notifData.notificationTypes.notificationTypes).some((entry: any) => entry?.enabled === true);
            }
        }

        // Fall back to global notification settings when no order-level data
        const globalTypes = currentUser?.notificationSettings?.notificationTypes;

        return globalTypes ? Object.values(globalTypes).some((entry: any) => entry?.enabled === true) : false;
    }, [textNotificationData, currentUser?.notificationSettings]);

    const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();

        if (order.masterOrderId) {
            // orderStatus seeds the detail page's stage switcher on first paint, before
            // the detail API resolves — see navStateOrderStatus in RadarOrderDetail.
            navigate(`${PATH.TRACK}/${order.masterOrderId}`, { state: { orderStatus: order?.orderStatus } });
        }
    };

    const handleMarkAsPriority = (e: React.MouseEvent) => {
        e.stopPropagation();

        const newPriorityValue = !order.isPriority;
        setIsPriorityLoading(true);

        updateOrderPriority(String(order.masterOrderId), newPriorityValue)
            .then(() => {
                dispatch(updateTrackOrder({ masterOrderFullId: order.masterOrderFullId, data: { isPriority: newPriorityValue } }));
                WalToast.success(newPriorityValue ? (t('Radar.Marked_as_priority') as string) : (t('Radar.Marked_as_normal') as string));
            })
            .catch((err: any) => {
                logError(err, {
                    errorMessage: err?.message || 'Error updating order priority',
                    errorLocation: 'TrackOrderCard: handleMarkAsPriority',
                    storeInDatabase: true,
                    logToConsole: true,
                });
                WalToast.error(t('Radar.Priority_update_failed') as string);
            })
            .finally(() => {
                setIsPriorityLoading(false);
            });
    };

    const handleBellClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsNotificationModalOpen(true);
    };

    // Send to (this load only) prefill: this user's saved per-load addresses, else their profile (cp-meeting-changes F2).
    const notificationDelivery = useMemo(() => getDeliveryDefaults((textNotificationData as any)?.notificationTypes, currentUser), [textNotificationData, currentUser]);

    const handleTextNotificationSave = async (notificationTypes: INotificationTypes, delivery?: IDeliveryPayload) => {
        const response = await saveOrderNotificationSettings(String(order.masterOrderId), notificationTypes, delivery);
        const saved = response?.data?.data?.metadata ?? response?.data?.metadata ?? null;
        setTextNotificationData(saved);
        dispatch(updateTrackOrder({ masterOrderFullId: order.masterOrderFullId, data: { textNotification: saved } }));
    };

    // Compute status strip: "Picked up on / Delivered on" when actual times available, else standard label
    const statusStrip = useMemo(() => getTrackStatusStrip(order, appearance?.timezone, t), [order, appearance?.timezone, t]);

    const isCompleted = order.radarSubStatus === 'completed';

    return (
        <>
            <div
                key={index}
                className={`rounded-lg cursor-pointer shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden ${isCompleted ? 'bg-white800' : 'bg-white'}`}
                onClick={handleCardClick}
                onMouseEnter={() => {
                    setIsHovered(true);
                    onHover?.();
                }}
                onMouseLeave={() => {
                    setIsHovered(false);
                    onHoverEnd?.();
                }}
            >
                {/* Row 1: Order ID + Badges + Action Icons */}
                <div className="px-4 pt-3 pb-3 flex items-center flex-wrap gap-x-2.5">
                    <div className="min-w-0 flex items-center gap-x-1.5">
                        {/* border-dot-common sits on the wrapper, not the id text, so the dotted
                            underline runs under the id AND the activity chevron, and the pair
                            shares one hover highlight — same as radar's card. */}
                        <div className="flex items-center border-dot-common overflow-hidden flex-none">
                            <div className={`flex items-center self-stretch hover:bg-utilityGray200 ${isLatestActivityTooltipOpen ? 'bg-utilityGray200' : ''}`}>
                                <TruncateTextCmp refName={orderIdRef} tooltipType="action" message={`#${order.masterOrderFullId}`} truncateTextParentClassName="flex items-center pr-1.5">
                                    <p ref={orderIdRef} className="text-grayLight900 text-sm leading-[18px] font-medium truncate cursor-pointer">
                                        #{order.masterOrderFullId}
                                    </p>
                                </TruncateTextCmp>
                                <div onClick={(e) => e.stopPropagation()} className="flex items-center">
                                    <TooltipCmp
                                        TooltipCustomClass="tooltipV3-content-wrap"
                                        isArrow={false}
                                        isTooltipInteractive
                                        parentClassName="tooltip-content-with-scroll-wrap w-[400px] space-y-2 text-left"
                                        openOnClick
                                        position="bottom"
                                        onOpenChange={setIsLatestActivityTooltipOpen}
                                        message={
                                            <ActivityDropdownList
                                                items={activityDisplayItems}
                                                isLoading={isActivityLoading}
                                                isLoadingMore={isActivityLoadingMore}
                                                hasMore={hasMoreActivity}
                                                onLoadMore={loadMoreActivity}
                                                listTitle="Latest Activity"
                                                emptyStateDesc="There is no activity recorded for this order yet."
                                            />
                                        }
                                    >
                                        <div className="cursor-pointer flex items-center py-1">
                                            <ChevronDown className="w-4 h-4 text-grayLight600" />
                                        </div>
                                    </TooltipCmp>
                                </div>
                            </div>
                        </div>
                        {/* Split leg card (Show-as-Multi-Leg ON): the master id repeats across sibling
                            cards, so surface the leg's own id — badged on the same line — to tell them apart */}
                        {order.isLegRow && order.legPublicId && (
                            <BadgeCmp style="modern" type="gray" isHidePillDot mainClassName="whitespace-nowrap flex-none">
                                #{order.legPublicId}
                            </BadgeCmp>
                        )}
                    </div>

                    <div className="flex flex-1 gap-x-2 gap-y-1">
                        {order.orderType && (
                            <BadgeCmp style="modern" type={getOrderTypeBadge(order.orderType)}>
                                {order.orderType.toUpperCase()}
                            </BadgeCmp>
                        )}
                        {order.orderStatus && (
                            <BadgeCmp style="modern" type={getBadgeType(order.orderStatus)}>
                                {order.orderStatus.charAt(0).toUpperCase() + order.orderStatus.slice(1).replace(/-/g, ' ')}
                            </BadgeCmp>
                        )}
                    </div>
                    <div className="flex items-center gap-x-2 flex-none">
                        <TooltipCmp message={order.isPriority ? t('Radar.Mark_as_Normal') : t('Radar.Mark_as_Priority')} childrenClassName="flex">
                            <span onClick={!isPriorityLoading ? handleMarkAsPriority : undefined} className="flex cursor-pointer">
                                {isPriorityLoading ? (
                                    <div className="animate-spin rounded-full w-4 h-4 border-2 border-primary border-t-transparent" />
                                ) : order.isPriority ? (
                                    <img src={LightingIcon} className="w-4 h-4" alt="Priority" />
                                ) : (
                                    <Lightning01 className="w-4 h-4 text-textSecondary" />
                                )}
                            </span>
                        </TooltipCmp>
                        {isTextNotificationFeatureVisible && (
                            <TooltipCmp message={t('Dashboard.NotificationSettings.Title')} childrenClassName="flex">
                                {isNotificationEnabled ? (
                                    <Bell01 className="w-4 h-4 cursor-pointer text-[#3538CD]" onClick={handleBellClick} />
                                ) : (
                                    <BellOff01 className="w-4 h-4 cursor-pointer text-textSecondary" onClick={handleBellClick} />
                                )}
                            </TooltipCmp>
                        )}
                    </div>
                </div>

                {/* Row 3: Shipper / Consignee Box */}
                <div className="px-4 pb-3">
                    <ShipperConsigneeBox order={order} isHovered={isHovered} />
                </div>

                <div className="px-4 pb-3 flex items-center justify-between gap-x-2">
                    <div className="flex items-center gap-x-1.5 min-w-[150px] overflow-hidden">
                        {order.carrier?.image ? (
                            <img src={order.carrier.image} className="table-profile-icn-circle-xs rounded-md flex-none" />
                        ) : isOrgCarrier ? (
                            <div className="table-profile-customer-circle-xs rounded-md !bg-utilityBlack flex items-center justify-center p-0.5 flex-none">
                                <img src={WalLogoIcon} alt={orgBranding.name} className="w-full h-full object-contain" />
                            </div>
                        ) : (
                            <div className="table-profile-customer-circle-xs rounded-md flex-none">{getShortName(carrierName)}</div>
                        )}
                        <TruncateTextCmp refName={carrierNameRef} tooltipType="action" message={carrierName || orgBranding.name}>
                            <p ref={carrierNameRef} className="text-xs text-textSecondary font-medium truncate flex-none max-w-full">
                                {carrierName || orgBranding.name}
                            </p>
                        </TruncateTextCmp>

                        {/* Tracking Link — same carrier-name | divider | link layout as WAL Radar's
                            map sidebar card. Present ONLY when WAL ticked "Show this tracking link
                            to the customer"; the portal backend sends null otherwise. */}
                        {order.trackingLink && (
                            <>
                                <span className="w-px bg-borderPrimary flex h-[16px] flex-none" />
                                <div className="flex-1 min-w-0 overflow-hidden">
                                    <TruncateTextCmp refName={trackingLinkRef} tooltipType="action" message={order.trackingLink}>
                                        <a
                                            ref={trackingLinkRef}
                                            href={order.trackingLink}
                                            target="_blank"
                                            // noreferrer alongside noopener: the carrier's tracking host must not
                                            // receive the portal URL (and the customer's order id) as a referrer.
                                            rel="noopener noreferrer"
                                            // The card itself navigates to the order on click — stop the event here
                                            // so opening the carrier's tracker doesn't also change the page behind it.
                                            onClick={(e) => e.stopPropagation()}
                                            className="block truncate text-xs text-primary font-normal hover:underline"
                                        >
                                            {order.trackingLink}
                                        </a>
                                    </TruncateTextCmp>
                                </div>
                            </>
                        )}
                    </div>
                    <StatusDotProfileCmp
                        labelRef={statusLabelRef}
                        icon={statusStrip.hasContent ? statusStrip.icon ?? undefined : undefined}
                        dotColor={statusStrip.hasContent ? statusStrip.dotColor : undefined}
                        label={statusStrip.hasContent ? statusStrip.label : ''}
                        statusLabelClassName="truncate"
                        parentClassName="min-w-0"
                    />
                </div>
            </div>
            {isNotificationModalOpen && (
                <NotificationSettingsModal
                    handleClose={() => setIsNotificationModalOpen(false)}
                    existingData={notificationExistingData}
                    onSave={handleTextNotificationSave}
                    delivery={notificationDelivery}
                />
            )}
        </>
    );
};

export default TrackOrderCard;
